import { Children, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { steuerRueckfrageAus } from "./SteuerRueckfrage";
import { useQueryClient } from "@tanstack/react-query";
import { kachelBetrag, money } from "../lib/format";
import { describeError } from "./States";
import { BETRAG_VERDECKT, useBetraegeSichtbar } from "../app/betraege";
import { dismissAnlass, toast } from "./Toast";
import type { Pill } from "../model/invoice-view";
import { t } from "../lib/sprache";
import { useBfcacheRueckkehr } from "../lib/bfcache";

/** Kleine Bausteine mit dem Markup des Design-Pakets (Status-Pille, sortierbare Tabelle, Werkzeugleiste, Dialog). */

export function PillView({ pill }: { pill: Pill }) {
  return <span className={`pill ${pill.tone}`}><span className="pill-zeichen">{pill.symbol}</span> {pill.label}</span>;
}

/** Betrag; im Agentur-Portal ohne das Recht „Beträge sehen“ verdeckt (RECHTE-6). */
export function Money({ cents, currency = "EUR" }: { cents: number | null | undefined; currency?: string }) {
  const sichtbar = useBetraegeSichtbar();
  if (!sichtbar) return <span className="num" title={t("Beträge ausgeblendet", "Amounts hidden")}>{BETRAG_VERDECKT}</span>;
  return <span className="num">{money(cents, currency)}</span>;
}

/** Betrag einer Kennzahl-Kachel nach der Kachelregel (lib/format.ts, kachelBetrag); gerundet mit dem genauen Betrag im Tooltip. */
export function KachelBetrag({ cents, currency = "EUR" }: { cents: number | null | undefined; currency?: string }) {
  const text = kachelBetrag(cents, currency);
  const genau = cents === null || cents === undefined || !Number.isFinite(cents) ? null : money(cents, currency);
  return <div className="metric num" title={genau && genau !== text ? genau : undefined}>{text}</div>;
}

export function PageHead({ title, text, eyebrow, children }: { title: ReactNode; text?: ReactNode; eyebrow?: string; children?: ReactNode }) {
  return (
    <div className="pagehead">
      <div>
        {eyebrow && <div className="eyebrow">{eyebrow}</div>}
        <h1>{title}</h1>
        {/* Kompakt-Ebene: einzeilig mit Auslassung, der volle Satz steht im Tooltip */}
        {text !== undefined && <p title={typeof text === "string" ? text : undefined}>{text}</p>}
      </div>
      {/* Revision 5: mehrere lose Aktionen als eine rechtsbündige Gruppe (sonst verteilt „space-between“ sie über die Zeile) */}
      {Children.toArray(children).length > 1 ? <div className="row tk-kopf-aktionen">{children}</div> : children}
    </div>
  );
}

/**
 * G2 (D-1, Inhaber 21.10.2026): seltene Aktionen im Seitenkopf hinter „Mehr“, damit der Kopf einzeilig bleibt. Schließt
 * bei Klick daneben, mit Escape und nach der Wahl eines Eintrags.
 */
export function KopfMehr({ children, label }: { children: ReactNode; label?: string }) {
  const ref = useRef<HTMLDetailsElement>(null);
  useEffect(() => {
    const zu = (e: Event) => { const d = ref.current; if (d?.open && !d.contains(e.target as Node)) d.open = false; };
    const esc = (e: globalThis.KeyboardEvent) => { if (e.key === "Escape" && ref.current?.open) { ref.current.open = false; ref.current.querySelector("summary")?.focus(); } };
    document.addEventListener("pointerdown", zu);
    document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("pointerdown", zu); document.removeEventListener("keydown", esc); };
  }, []);
  return (
    <details ref={ref} className="tk-kopf-mehr">
      <summary className="btn">{label ?? t("Mehr", "More")}</summary>
      <div className="tk-kopf-mehr-menue" onClick={(e) => { if ((e.target as HTMLElement).closest("a, button") && ref.current) ref.current.open = false; }}>{children}</div>
    </details>
  );
}

/** Mailadresse in Listen: darf nur vor dem „@“ umbrechen (Trennstelle wie im Satz üblich), ganz im Tooltip. */
export function MailAdresse({ adresse }: { adresse: string }) {
  const at = adresse.lastIndexOf("@");
  return <span className="tk-mail" title={adresse}>{at > 0 ? <>{adresse.slice(0, at)}<wbr />{adresse.slice(at)}</> : adresse}</span>;
}

// ── Sortierbare Tabelle (Kopf wie im Paket: <th scope="col"><button>Beschriftung ↕</button></th>) ──

export type SortValue = string | number | null | undefined;
export interface SortState<K extends string> {
  key: K | null;
  dir: 1 | -1;
}

export function useSorted<T, K extends string>(rows: readonly T[], accessors: Record<K, (row: T) => SortValue>, initial: SortState<K> = { key: null, dir: 1 }) {
  const [sort, setSort] = useState<SortState<K>>(initial);
  const sorted = useMemo(() => {
    if (!sort.key) return rows;
    const get = accessors[sort.key];
    return [...rows].sort((a, b) => {
      const x = get(a), y = get(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      const c = typeof x === "number" && typeof y === "number" ? x - y : String(x).localeCompare(String(y), "de", { numeric: true });
      return c * sort.dir;
    });
    // accessors sind je Seite konstant
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sort]);
  const toggle = (key: K) => setSort((s) => (s.key === key ? { key, dir: s.dir === 1 ? -1 : 1 } : { key, dir: 1 }));
  return { sorted, sort, toggle };
}

/** Pfeil hinter dem Spaltennamen: ↑/↓ für die aktive Sortierung, sonst ↕; das geschützte Leerzeichen hält ihn am Wort */
function sortPfeil<K extends string>(sort: SortState<K>, k: K): string {
  return `\u00a0${sort.key === k ? (sort.dir === 1 ? "↑" : "↓") : "↕"}`;
}

export function SortTh<K extends string>({ label, k, sort, toggle, money: isMoney, className }: { label: string; k: K; sort: SortState<K>; toggle: (k: K) => void; money?: boolean; className?: string }) {
  const aria = sort.key === k ? (sort.dir === 1 ? "ascending" : "descending") : undefined;
  const cls = [isMoney ? "money" : "", className ?? ""].filter(Boolean).join(" ") || undefined;
  return (
    <th scope="col" className={cls} aria-sort={aria}>
      <button className={sort.key === k ? "tk-sort-aktiv" : undefined} onClick={() => toggle(k)}>{label}<span className="tk-sort-pfeil">{sortPfeil(sort, k)}</span></button>
    </th>
  );
}

/**
 * Spalte mit Wert und Nebenwert (z. B. Vorjahr und Veränderung): zwei Sortierknöpfe untereinander, damit breite Auswertungen
 * bei 1280 px passen, ohne dass eine Sortierung wegfällt. Die Zelle zeigt den Hauptwert und darunter den Nebenwert (small).
 */
export function SortThDoppelt<K extends string>({ label, k, neben, kNeben, sort, toggle, money: isMoney }: {
  label: string; k: K; neben: string; kNeben: K; sort: SortState<K>; toggle: (k: K) => void; money?: boolean;
}) {
  const aria = sort.key === k || sort.key === kNeben ? (sort.dir === 1 ? "ascending" : "descending") : undefined;
  return (
    <th scope="col" className={`tk-th-doppelt${isMoney ? " money" : ""}`} aria-sort={aria}>
      {/* welcher der beiden Werte sortiert, zeigen Pfeil und Hervorhebung; die Richtung meldet aria-sort am Kopf */}
      <button className={sort.key === k ? "tk-sort-aktiv" : undefined} onClick={() => toggle(k)}>{label}<span className="tk-sort-pfeil">{sortPfeil(sort, k)}</span></button>
      <button className={sort.key === kNeben ? "tk-sort-aktiv" : undefined} onClick={() => toggle(kNeben)}>{neben}<span className="tk-sort-pfeil">{sortPfeil(sort, kNeben)}</span></button>
    </th>
  );
}

/**
 * K-465: Sortieren auf dem Handy. In der Kartenansicht ist der Tabellenkopf ausgeblendet; diese Auswahl steht direkt vor der
 * Liste (nur unter 768 px sichtbar), liest die Sortierknöpfe aus dem Kopf der folgenden Tabelle und bedient sie. So gibt es
 * keine zweite Spaltenliste, und Sortierung und Pfeile im Kopf bleiben dieselben.
 */
export function SortWahl() {
  const ref = useRef<HTMLLabelElement>(null);
  const [opts, setOpts] = useState<string[]>([]);
  const [wert, setWert] = useState("");
  const knoepfe = (): HTMLButtonElement[] => {
    const next = ref.current?.nextElementSibling;
    const table = next?.tagName === "TABLE" ? next : next?.querySelector("table");
    return table ? [...table.querySelectorAll<HTMLButtonElement>("thead th button")] : [];
  };
  const name = (b: HTMLButtonElement) => (b.textContent ?? "").replace(/[\u00a0\s]*[↕↑↓]\s*$/, "").replace(/\u00ad/g, "").trim();
  const lesen = () => {
    const bs = knoepfe();
    setOpts(bs.map(name));
    const i = bs.findIndex((b) => /[↑↓]\s*$/.test(b.textContent ?? ""));
    setWert(i < 0 ? "" : `${i}:${/↑\s*$/.test(bs[i]!.textContent ?? "") ? "auf" : "ab"}`);
  };
  useEffect(() => {
    lesen();
    const next = ref.current?.nextElementSibling;
    if (!next) return;
    const mo = new MutationObserver(lesen);
    mo.observe(next, { subtree: true, childList: true, characterData: true });
    return () => mo.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const waehlen = (v: string) => {
    const [i, richtung] = v.split(":");
    const b = knoepfe()[Number(i)];
    if (!b) return;
    const jetzt = (b.textContent ?? "").trim();
    // Erster Klick sortiert aufsteigend, ein weiterer kehrt um (useSorted.toggle zählt mit, auch bei zwei Klicks hintereinander)
    if (/↑$/.test(jetzt)) { if (richtung === "ab") b.click(); }
    else if (/↓$/.test(jetzt)) { if (richtung === "auf") b.click(); }
    else { b.click(); if (richtung === "ab") b.click(); }
  };
  return (
    <label ref={ref} className="tk-sort-wahl" hidden={!opts.length}>
      <span>{t("Sortieren nach", "Sort by")}</span>
      <select value={wert} onChange={(e) => waehlen(e.target.value)}>
        {wert === "" && <option value="">{t("Standard", "Default")}</option>}
        {opts.flatMap((o, i) => [
          <option key={`${i}a`} value={`${i}:auf`}>{o} ↑</option>,
          <option key={`${i}b`} value={`${i}:ab`}>{o} ↓</option>,
        ])}
      </select>
    </label>
  );
}

// ── Werkzeugleiste mit Suche und Filterknöpfen ──

export function Toolbar<F extends string>({ search, onSearch, placeholder, label, filters, filter, onFilter, children }: {
  search: string;
  onSearch: (v: string) => void;
  placeholder: string;
  label: string;
  filters?: readonly { key: F; label: string; count?: number }[];
  filter?: F;
  onFilter?: (f: F) => void;
  children?: ReactNode;
}) {
  return (
    <div className="toolbar">
      <input type="search" aria-label={label} placeholder={placeholder} value={search} onChange={(e) => onSearch(e.target.value)} />
      {filters?.map((f) => (
        <button key={f.key} aria-pressed={filter === f.key} data-filter={f.key} onClick={() => onFilter?.(f.key)}>
          {f.label}
          {/* Revision 5: Zähler als kleines Kennzeichen wie im Original; der Trenner bleibt für Vorleser und Textsuche erhalten */}
          {f.count !== undefined && f.count > 0 ? <b className="tk-chip-count"><span className="sr-only"> · </span>{f.count}</b> : ""}
        </button>
      ))}
      {children}
    </div>
  );
}

export function matchesSearch(text: string, query: string): boolean {
  const q = query.trim().toLowerCase();
  return !q || text.toLowerCase().includes(q);
}

// ── Dialog (Modal aus dem Paket: <dialog>, max. 560 px, Fokus zurück zum Auslöser) ──

export function Dialog({ open, onClose, title, children, wide }: { open: boolean; onClose: () => void; title: string; children: ReactNode; wide?: boolean }) {
  const ref = useRef<HTMLDialogElement>(null);
  const trigger = useRef<Element | null>(null);
  const byCode = useRef(false);
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) {
      trigger.current = document.activeElement;
      d.showModal();
      // K-555 d: Fokus auf das erste Eingabefeld (Grund, Betrag, Datum …) statt auf „Schließen“; ohne Feld bleibt es beim Browser.
      // Ein Feld mit autoFocus hat Vorrang.
      window.setTimeout(() => {
        if (!d.open || d.querySelector("[autofocus]")) return;
        const feld = d.querySelector<HTMLElement>("input:not([type=hidden]):not([type=checkbox]):not([type=radio]):not([disabled]), textarea:not([disabled]), select:not([disabled])");
        feld?.focus();
      }, 0);
    } else if (!open && d.open) {
      // Schließen durch die Seite selbst (open → false) meldet kein onClose: sonst überschreibt es einen Folgedialog
      byCode.current = true;
      d.close();
      (trigger.current as HTMLElement | null)?.focus?.();
    }
  }, [open]);
  const userClose = () => {
    if (byCode.current) {
      byCode.current = false;
      return;
    }
    onClose();
  };
  return (
    <dialog ref={ref} aria-labelledby={titleId} onClose={userClose} onCancel={(e) => { e.preventDefault(); onClose(); }} className={wide ? "tk-wide" : undefined}>
      {open && (
        <div className="stack">
          {/* Kopf als Raster Titel | Knopf: „Schließen“ bleibt rechts oben, auch wenn der Titel umbricht (SICHT-OEFFENTLICH-BETRIEB-23) */}
          <div className="tk-dialog-head">
            <h2 id={titleId}>{title}</h2>
            <button type="button" onClick={onClose}>{t("Schließen", "Close")}</button>
          </div>
          {children}
        </div>
      )}
    </dialog>
  );
}

/**
 * Rückfrage im Portal-Dialog statt window.confirm (Portal einheitlich, 30.09.2026): Das Browserfenster sah je Browser
 * anders aus, trug keine Marke und blieb im Dunkelmodus hell. `ask` liefert true bei „Fortfahren“, false bei Abbrechen
 * oder Schließen; `element` gehört einmal in die Seite.
 */
export function useRueckfrage(): { ask: (frage: { title: string; text: string; confirmLabel?: string; danger?: boolean }) => Promise<boolean>; element: ReactNode } {
  const [offen, setOffen] = useState<{ title: string; text: string; confirmLabel?: string; danger?: boolean } | null>(null);
  const antwort = useRef<((ja: boolean) => void) | null>(null);
  const ende = (ja: boolean) => {
    antwort.current?.(ja);
    antwort.current = null;
    setOffen(null);
  };
  const ask = (frage: { title: string; text: string; confirmLabel?: string; danger?: boolean }) => new Promise<boolean>((resolve) => {
    antwort.current?.(false);
    antwort.current = resolve;
    setOffen(frage);
  });
  const element = (
    <Dialog open={!!offen} onClose={() => ende(false)} title={offen?.title ?? ""}>
      {offen?.text.split("\n").map((zeile, i) => <p key={i}>{zeile}</p>)}
      <div className="row">
        <button type="button" className={offen?.danger ? "danger" : "primary"} onClick={() => ende(true)}>{offen?.confirmLabel ?? t("Fortfahren", "Continue")}</button>
        <button type="button" onClick={() => ende(false)}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </Dialog>
  );
  return { ask, element };
}

// ── Aktionen mit Ladezustand, Erfolgsmeldung und Fehler im Klartext ──

export function useAction() {
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  // Praxistest K-097: Ein Doppelklick startete dieselbe Aktion zweimal (Erfolg und Fehler zugleich); der Zustand „busy“ sperrt den
  // Knopf erst nach dem nächsten Zeichnen, die Sperre hier greift sofort
  const laufend = useRef(new Set<string>());
  // Zurück aus Stripe bzw. PayPal (Seite aus dem bfcache): Aktionen, die mit der Weiterleitung endeten, wieder freigeben
  useBfcacheRueckkehr(() => { laufend.current.clear(); setBusy(null); });
  // P-1 (21.10.2026): Fehlermeldungen einer Aktion verschwinden nach einem erfolgreichen Versuch derselben Aktion; Kennung aus Seite
  // und Aktion, damit sie auch gilt, wenn ein Dialog dazwischen neu geöffnet wurde
  const anlass = (key: string) => `${typeof window !== "undefined" ? window.location.pathname : ""}:${key}`;
  // Prüf-Chat CP-010 (02.11.2026): `nichtWarten` gibt den Knopf frei, ohne auf das Neuladen aller Abfragen zu warten (das stand
  // bei „Einladung erstellen“ 5–8 s auf „Wird gespeichert …“); neu geladen wird trotzdem
  async function run<T>(key: string, fn: () => Promise<T>, success?: string, opts?: { nichtWarten?: boolean }): Promise<T | undefined> {
    if (laufend.current.has(key)) return undefined;
    laufend.current.add(key);
    setBusy(key);
    setError(null);
    try {
      const r = await fn();
      dismissAnlass(anlass(key));
      if (success) toast(success);
      const neu = qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "account" });
      if (!opts?.nichtWarten) await neu;
      return r;
    } catch (e) {
      // 0247/K-183: noch unbestätigte Adresse ist kein Fehler, sondern ein erwarteter Zustand: ruhiger Hinweis, kein „Melden“
      if ((e as { hint?: string } | null)?.hint === "verify:email") {
        toast((e as Error).message, "status");
        return undefined;
      }
      // 0269 (Block B): Steuer-Rückfrage statt Sperre; nach der Bestätigung läuft derselbe Schritt erneut
      if (steuerRueckfrageAus(e, () => run(key, fn, success, opts))) return undefined;
      // Abnahme L12 (E-4): Eingabefehler (z. B. E-Rechnung mit DTD, zu groß, beschädigt) bleiben stehen, ohne „Melden“
      if ((e as { melden?: boolean } | null)?.melden === false) {
        setError((e as Error).message);
        toast((e as Error).message, "alert", anlass(key), { melden: false });
        return undefined;
      }
      const d = describeError(e);
      setError(`${d.title} ${d.text}`.trim());
      toast((!d.title || d.title === "Das hat nicht geklappt." || d.title === "That did not work.") ? d.text : `${d.title} ${d.text}`, "alert", anlass(key));
      return undefined;
    } finally {
      laufend.current.delete(key);
      setBusy(null);
    }
  }
  /** Wie run, meldet aber nur Erfolg (true) oder Fehler (false); für Aktionen ohne Rückgabewert. */
  async function attempt(key: string, fn: () => Promise<unknown>, success?: string): Promise<boolean> {
    let ok = false;
    await run(key, async () => {
      await fn();
      ok = true;
    }, success);
    return ok;
  }
  return { busy, error, run, attempt, clearError: () => setError(null) };
}
